import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  Activity,
  Hammer,
  AlertOctagon,
  Bell,
  FolderKanban,
  TrendingUp,
  ChevronRight,
} from "lucide-react";

const RATING_COLOR = (r) =>
  r >= 75 ? "#10B981" : r >= 50 ? "#F59E0B" : "#EF4444";

const StatTile = ({ icon: Icon, label, value, accent = "#004D99", testid }) => (
  <div
    className="bg-white border border-slate-200 rounded-lg p-5 hover:border-[#004D99] hover:-translate-y-0.5 transition-all"
    data-testid={testid}
  >
    <Icon className="w-5 h-5 mb-3" style={{ color: accent }} />
    <div className="font-mono text-3xl font-black tracking-tight">{value}</div>
    <div className="label-overline mt-1">{label}</div>
  </div>
);

export default function AAYDADashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/aayda").then((r) => setData(r.data));
  }, []);

  if (!data)
    return <div className="mono text-sm text-slate-500">Yüklənir...</div>;

  return (
    <div className="space-y-6" data-testid="aayda-dashboard">
      {/* Headline tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          icon={FolderKanban}
          label="Ongoing layihələr"
          value={data.ongoing_projects}
          accent="#004D99"
          testid="aayda-tile-ongoing"
        />
        <StatTile
          icon={AlertOctagon}
          label="Kritik məsələ"
          value={data.critical_total}
          accent="#EF4444"
          testid="aayda-tile-critical"
        />
        <StatTile
          icon={Bell}
          label="Aktiv xəbərdarlıq"
          value={data.alerts.length}
          accent="#F59E0B"
          testid="aayda-tile-alerts"
        />
        <StatTile
          icon={Hammer}
          label="Podratçı sayı"
          value={data.contractors_count}
          accent="#FF5722"
          testid="aayda-tile-contractors"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Progress bar chart spanning 2 columns */}
        <div
          className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5"
          data-testid="aayda-progress-chart"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base">Layihə icra göstəriciləri</h3>
            <span className="mono text-[10px] text-slate-500">son orta % seqment</span>
          </div>
          <div className="h-72 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.progress}
                margin={{ top: 8, right: 8, left: -16, bottom: 36 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 9, fontFamily: "monospace" }}
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 10, fontFamily: "monospace" }}
                  domain={[0, 100]}
                  unit="%"
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    border: "1px solid #E2E8F0",
                  }}
                />
                <Bar dataKey="completion" name="İcra %" fill="#004D99" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top contractors */}
        <div
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="aayda-top-contractors"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base">Top podratçılar</h3>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </div>
          <div className="space-y-2">
            {data.top_contractors.length === 0 && (
              <div className="text-xs text-slate-400 py-4 text-center">
                Reytinq məlumatı yoxdur
              </div>
            )}
            {data.top_contractors.map((c, i) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 p-2 rounded hover:bg-slate-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-7 h-7 rounded font-mono text-xs font-black flex items-center justify-center text-white shrink-0"
                    style={{ background: RATING_COLOR(c.rating) }}
                  >
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{c.name}</div>
                    <div className="text-[10px] mono text-slate-500">
                      OK {c.ok} · NOT OK {c.not_ok}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-lg font-black">
                    {c.rating.toFixed(0)}
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Critical issues */}
        <div
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="aayda-critical-issues"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base">Kritik məsələlər</h3>
            <span className="mono text-[10px] text-red-600 font-bold">
              {data.critical_total} açıq
            </span>
          </div>
          {data.critical_issues.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">
              Kritik məsələ yoxdur
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.critical_issues.map((c) => (
                <Link
                  key={c.id}
                  to={`/inspections/${c.id}`}
                  className="flex items-center justify-between gap-2 p-2.5 border border-red-100 bg-red-50/40 rounded hover:bg-red-50 transition-colors"
                  data-testid={`critical-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {c.project_name}
                    </div>
                    <div className="mono text-[10px] text-slate-500">
                      {c.stage_type || "—"} ·{" "}
                      <span
                        className={
                          c.kind === "rejected" ? "text-red-700" : "text-amber-700"
                        }
                      >
                        {c.kind === "rejected" ? "REDD EDİLİB" : "KORREKSIYA"}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div
          className="bg-white border border-slate-200 rounded-lg p-5"
          data-testid="aayda-alerts"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base">Aktiv xəbərdarlıqlar</h3>
            <Bell className="w-4 h-4 text-amber-500" />
          </div>
          {data.alerts.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">
              Aktiv xəbərdarlıq yoxdur
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.alerts.map((a, i) => {
                const color = a.level === "critical" ? "red" : "amber";
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-2.5 border rounded ${
                      color === "red"
                        ? "border-red-200 bg-red-50/40"
                        : "border-amber-200 bg-amber-50/40"
                    }`}
                    data-testid={`alert-${i}`}
                  >
                    <Activity
                      className={`w-4 h-4 mt-0.5 shrink-0 ${
                        color === "red" ? "text-red-600" : "text-amber-600"
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{a.title}</div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {a.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
